import { Row, Select, SelectOption, SelectOptionGroup } from "@amzn/meridian";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import React from "react";
import {
  getChildOptions,
  getGroundTruthDataReviewStatus,
  getOptionLabel,
  isFieldConfirmedAnswerValid,
} from "src/components/shared/groundTruth/reviewTable/helpers";
import { INADEQUATE_INFORMATION, NO_OBJECT_INVOLVED, OTHER } from "src/constants";
import { createSearchRegExp } from "src/helpers";
import {
  GroundTruthField,
  ReviewResult,
  setFieldConfirmedAnswer,
} from "src/store/modelLifeCycleGroundTruthSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { compareStringsIgnoreCase, hasOverlapCaseInsensitive } from "src/utils/stringUtil";
import { getConfirmedAnswer, isFieldMultiSelected } from "../../helpers";
import { getInadequateSelection, otherSelection } from "./selectionHelpers";

interface ChildFieldProps {
  groundTruthField: GroundTruthField;
  fieldIndex: number;
  value: string | string[];
  hasInadequateInfo: boolean;
  hasOtherOption: boolean;
  searchQuery: string;
  shouldShowError: boolean;
  setSearchQuery: (val: string) => void;
}
const ChildField = ({
  groundTruthField,
  fieldIndex,
  value,
  hasInadequateInfo,
  shouldShowError,
  hasOtherOption,
  searchQuery,
  setSearchQuery,
}: ChildFieldProps) => {
  const dispatch = useAppDispatch();
  const { alias } = useAppSelector((state) => state.user);
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const { selectedGroundTruthData } = useAppSelector(
    (state) => state.modelLifeCycleGroundTruthSlice,
  );
  const searchRegExp = createSearchRegExp(searchQuery);

  // If this is a child field, we need to handle parent-child relationship
  const hierarchyRule = selectedModel?.hierarchy?.find(
    (h) => h.childFieldName === groundTruthField.fieldName,
  );

  const parentField = selectedGroundTruthData.fields.find(
    (field) => field.fieldName === hierarchyRule?.parentFieldName,
  );
  if (!parentField?.confirmedAnswer || parentField?.confirmedAnswer.length === 0) {
    return <Text type={"b100"}>Please select {hierarchyRule?.parentFieldName}</Text>;
  }

  // Get available options based on parent selection
  const availableChildOptions = getChildOptions(
    selectedModel.hierarchy,
    hierarchyRule?.parentFieldName || "",
    groundTruthField.fieldName,
    parentField.confirmedAnswer,
    hasInadequateInfo,
    hasOtherOption,
  );
  const isParentSelectInadequateInfo = Array.isArray(parentField.confirmedAnswer)
    ? parentField.confirmedAnswer.some((answer) =>
        compareStringsIgnoreCase(answer, INADEQUATE_INFORMATION),
      )
    : compareStringsIgnoreCase(parentField.confirmedAnswer, INADEQUATE_INFORMATION);

  const isParentSelectNoObjectInvolved = Array.isArray(parentField.confirmedAnswer)
    ? parentField.confirmedAnswer.some((answer) =>
        compareStringsIgnoreCase(answer, NO_OBJECT_INVOLVED),
      )
    : compareStringsIgnoreCase(parentField.confirmedAnswer, NO_OBJECT_INVOLVED);

  if (isParentSelectInadequateInfo) {
    dispatch(
      setFieldConfirmedAnswer({
        fieldIndex: fieldIndex,
        confirmedAnswer: getConfirmedAnswer(
          groundTruthField,
          `${INADEQUATE_INFORMATION}---${INADEQUATE_INFORMATION}`,
          isFieldMultiSelected(groundTruthField, selectedModel),
        ),
        reviewResult: [value],
      }),
    );
    return (
      <Select disabled={true} value={INADEQUATE_INFORMATION} size={"small"}>
        <SelectOption label={INADEQUATE_INFORMATION} value={INADEQUATE_INFORMATION} />
      </Select>
    );
  }

  if (isParentSelectNoObjectInvolved) {
    dispatch(
      setFieldConfirmedAnswer({
        fieldIndex: fieldIndex,
        confirmedAnswer: getConfirmedAnswer(
          groundTruthField,
          `${NO_OBJECT_INVOLVED}---${NO_OBJECT_INVOLVED}`,
          isFieldMultiSelected(groundTruthField, selectedModel),
        ),
        reviewResult: [value],
      }),
    );
    return (
      <Select disabled={true} value={NO_OBJECT_INVOLVED} size={"small"}>
        <SelectOption label={NO_OBJECT_INVOLVED} value={NO_OBJECT_INVOLVED} />
      </Select>
    );
  }

  return (
    <div>
      {hierarchyRule && Array.isArray(parentField.confirmedAnswer) ? (
        parentField.confirmedAnswer.map((parentValue: string) => {
          const childOptions = getChildOptions(
            selectedModel.hierarchy,
            hierarchyRule.parentFieldName,
            groundTruthField.fieldName,
            parentValue,
            hasInadequateInfo,
            hasOtherOption,
          );
          return (
            <Row alignmentVertical={"bottom"} spacingInset={"0 0 400 0"}>
              <Column spacing={"0 0 0 0"}>
                <Text type="b100">{`Select option for ${parentValue}`}</Text>
                <Select
                  value={value}
                  onChange={(answer) => {
                    if (Array.isArray(answer)) {
                      answer = answer.filter((option: string) => option);
                    }
                    dispatch(
                      setFieldConfirmedAnswer({
                        fieldIndex: fieldIndex,
                        confirmedAnswer: answer,
                        reviewResult: ReviewResult.NEITHER,
                      }),
                    );
                  }}
                  placeholder={`Select option for ${parentValue}`}
                  size="small"
                  searchQuery={searchQuery}
                  onSearch={setSearchQuery}
                  error={
                    shouldShowError &&
                    (!isFieldConfirmedAnswerValid(groundTruthField.confirmedAnswer) ||
                      !hasOverlapCaseInsensitive(childOptions, groundTruthField.confirmedAnswer))
                  }
                  errorMessage={
                    shouldShowError &&
                    (!isFieldConfirmedAnswerValid(groundTruthField.confirmedAnswer) ||
                      !hasOverlapCaseInsensitive(childOptions, groundTruthField.confirmedAnswer)) &&
                    "please select a valid answer"
                  }
                >
                  {hasInadequateInfo && getInadequateSelection(parentValue)}
                  <SelectOptionGroup label={"Options"}>
                    {childOptions
                      .filter((option) => searchRegExp.test(option))
                      .filter(
                        (option) =>
                          !compareStringsIgnoreCase(getOptionLabel(option), INADEQUATE_INFORMATION),
                      )
                      .filter((option) => !compareStringsIgnoreCase(getOptionLabel(option), OTHER))
                      .filter(
                        (option) =>
                          !compareStringsIgnoreCase(getOptionLabel(option), NO_OBJECT_INVOLVED),
                      )
                      .map((option) => (
                        <SelectOption key={option} value={option} label={getOptionLabel(option)} />
                      ))}
                  </SelectOptionGroup>
                  {hasOtherOption &&
                    otherSelection(
                      parentValue,
                      groundTruthField.fieldName,
                      getGroundTruthDataReviewStatus(selectedGroundTruthData, alias),
                    )}
                </Select>
              </Column>
              <div className="confirmedAnswerContainer">
                {Array.isArray(groundTruthField.confirmedAnswer) &&
                  Object.values(groundTruthField.confirmedAnswer).filter((answer) =>
                    childOptions.includes(answer),
                  ).length > 1 &&
                  Object.values(groundTruthField.confirmedAnswer)
                    .filter((answer) => childOptions.includes(answer))
                    .map((answer) => (
                      <div className="confirmedAnswer">
                        <Button size={"small"} type={"tertiary"}>
                          {getOptionLabel(answer)}
                        </Button>
                      </div>
                    ))}
              </div>
            </Row>
          );
        })
      ) : (
        // Single parent selection
        <Row alignmentVertical={"bottom"}>
          <Column spacing={"0 0 0 0"}>
            <Text type="b100">{`Select option for ${parentField.confirmedAnswer}`}</Text>
            <Select
              value={value}
              onChange={(answer) => {
                if (Array.isArray(answer)) {
                  answer = answer.filter((option: string) => option);
                }
                dispatch(
                  setFieldConfirmedAnswer({
                    fieldIndex: fieldIndex,
                    confirmedAnswer: answer,
                    reviewResult: ReviewResult.NEITHER,
                  }),
                );
              }}
              placeholder="Select answer..."
              size="small"
              searchQuery={searchQuery}
              onSearch={setSearchQuery}
            >
              <SelectOptionGroup label="Options">
                {availableChildOptions
                  .filter((option) => searchRegExp.test(option))
                  .filter((option) => !compareStringsIgnoreCase(option, INADEQUATE_INFORMATION))
                  .filter((option) => !compareStringsIgnoreCase(option, OTHER))
                  .filter((option) => !compareStringsIgnoreCase(option, NO_OBJECT_INVOLVED))
                  .map((option) => (
                    <SelectOption key={option} value={option} label={option} />
                  ))}
              </SelectOptionGroup>
            </Select>
          </Column>
          <div className="confirmedAnswerContainer">
            {Array.isArray(groundTruthField.confirmedAnswer) &&
              Object.values(groundTruthField.confirmedAnswer).filter((answer) =>
                availableChildOptions.includes(answer),
              ).length > 1 &&
              Object.values(groundTruthField.confirmedAnswer)
                .filter((answer) => availableChildOptions.includes(answer))
                .map((answer) => (
                  <div className="confirmedAnswer">
                    <Button size={"small"} type={"tertiary"}>
                      {getOptionLabel(answer)}
                    </Button>
                  </div>
                ))}
          </div>
        </Row>
      )}
    </div>
  );
};

export default ChildField;
